// "About this screen" on every page (H-167; docs/superpowers/plans/2026-10-19-guide-and-screen-help-plan.md): what a
// screen is for and who can do what there, in one registry keyed by route name, plus the screens that share a route (the
// ticket's views, the kinds of connection being added). The page header's help reads it today; the guide and the helper
// agent (H-168) will read the same entries. The words are in screens.content.ts; tests/screens.test.ts keeps every
// route covered.
import { isTicketView } from "./tickets";
import { ADMINS_ONLY, SCREEN_CONTENT } from "./screens.content";

export type Who = "admin" | "workspaceAdmin" | "member" | "noWorkspace" | "viewer";
export interface ScreenHelp {
  title: string;                       // the screen's name as people say it
  purpose: string;                     // what it's for, one or two sentences
  who: Partial<Record<Who, string>>;   // what each kind of person can do here; a viewer's sentence defaults (whoSays)
}

// A Google sign-in is a viewer: in no workspace it only reads, and the API refuses the rest; once a workspace's admin adds
// it, it works there as the workspace's members do (Amine, 2026-10-19), so the server calls it a user and this row is
// never the reader's then.
export const VIEWER_READS = "You read what this page shows. To ask, start or approve, a workspace's admin adds you to their workspace.";

export const WHO: { key: Who; label: string }[] = [
  { key: "admin", label: "Organisation admins" },
  { key: "workspaceAdmin", label: "Workspace admins" },
  { key: "member", label: "Workspace members" },
  { key: "noWorkspace", label: "In no workspace" },
  { key: "viewer", label: "Read-only sign-ins (in no workspace)" },
];

export const NEW_CONNECTION_KINDS = ["cloudwatch", "database", "snowflake", "mcp", "agentcore"] as const;

interface RouteLike { name?: unknown; params: Record<string, unknown>; query: Record<string, unknown> }

/** The screen a route shows: its name, the ticket's view, or the kind of connection being added. */
export function screenKey(route: RouteLike): string | null {
  if (typeof route.name !== "string") return null;
  if (route.name === "ticket") return isTicketView(route.query.view) ? `ticket:${route.query.view}` : "ticket";
  if (route.name === "connector" && route.params.name === "new") {
    const kind = String(route.query.kind ?? "");
    return (NEW_CONNECTION_KINDS as readonly string[]).includes(kind) ? `connector:new:${kind}` : "connector:new";
  }
  return route.name;
}

export function screenHelp(route: RouteLike, content: Record<string, ScreenHelp> = SCREEN_CONTENT): ScreenHelp | null {
  const key = screenKey(route);
  return key ? content[key] ?? content[key.split(":")[0]] ?? null : null;
}

/** What a kind of person can do on a screen: its own sentence, else for a viewer the same as everyone on a page that
 * says the same to all (sign-in, not found), admins-only where it is, and otherwise that a viewer only reads. */
export function whoSays(help: ScreenHelp, who: Who): string {
  const said = help.who[who];
  if (said || who !== "viewer") return said ?? "";
  const others = new Set([help.who.member, help.who.noWorkspace]);
  if (others.size === 1 && help.who.member === help.who.admin) return help.who.member ?? VIEWER_READS;
  return help.who.member === ADMINS_ONLY ? ADMINS_ONLY : VIEWER_READS;
}

/** Which row of "who can do what" is the reader's: the server's role first, then the workspace they work in. */
export function readerIs(role: string | undefined, teams: { team_admin: boolean }[] | undefined, current: { team_admin: boolean } | null): Who {
  if (role === "admin") return "admin";
  if (role === "viewer") return "viewer";
  if (current?.team_admin) return "workspaceAdmin";
  return teams?.length ? "member" : "noWorkspace";
}
